import { useQuery } from '@tanstack/react-query'
import { createApiCall } from 'services'

export interface TagInventoryOverviewRow {
  id: string
  tag_shop_product: {
    id: string
    sku: string
    title: string
    images: string[]
    variants: Array<{ id: string; sku: string; title: string; quantity: number; price: number; unit_price: number; images: string[] }>
  }
  /**
   * The THREE POSSESSION STAGES per tag SKU, from `tags.possession_state`. A tag is in
   * exactly one, so they are mutually exclusive and they ADD UP:
   *
   *   at_vera_qty   — "Ordered":   paid for, Vera is still preparing it
   *   incoming_qty  — "Incoming":  shipped, on its way
   *   available_qty — "Available": received, usable now
   *
   * `company_tag_inventory.ordered_qty` is gone from this surface (VERA-455). It counted
   * LIFETIME purchases and nothing decremented it, so it could never reconcile against a
   * possession count — which is why "Ordered 100" sat beside "On hand 0" and read as a bug.
   */
  at_vera_qty: number
  incoming_qty: number
  available_qty: number
  /** `available_qty − uncertified_qty`. How many more units this SKU can certify today. */
  capacity: number
  /**
   * Σ in-stock quantity of assigned products still needing a tag — the level bar's DEMAND.
   * Present on the API response since VRG-90 and read by `InventoryLevelCell`; it was simply
   * missing from this interface, so every consumer saw it as a type error or an `any`.
   */
  uncertified_qty: number
  for_products: Array<{ id: string; title: string; sku: string; quantity: number; images: string[] }>
  threshold: number
  threshold_type: string
  auto_order: boolean
  send_threshold_email: boolean
  auto_order_variant: { quantity: number } | null
}

export interface TagInventoryOverview {
  /**
   * Company-wide headlines. `ordered` / `incoming` / `available` are the possession COUNTS
   * over every tag the company owns — not sums of the rows, which only carry tags whose
   * package has a tag-shop association. So the cards can exceed Σ of the columns; that gap
   * is unattributed stock, and reporting it is better than dropping it.
   */
  cards: {
    ordered: number
    incoming: number
    available: number
    capacity: number
    uncertifiedProducts: number
    atRisk: number
  }
  rows: TagInventoryOverviewRow[]
}

const EMPTY_CARDS = {
  ordered: 0,
  incoming: 0,
  available: 0,
  capacity: 0,
  uncertifiedProducts: 0,
  atRisk: 0,
}

export const useTagInventoryOverview = () => {
  const query = useQuery({
    queryKey: ['tag-inventory', 'overview'],
    queryFn: async () => {
      const response = await createApiCall({
        method: 'GET',
        url: '/shop/tags/inventory/overview',
      })
      return response.data as TagInventoryOverview
    },
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  })

  return {
    // MERGED over the defaults, not `?? EMPTY_CARDS`. The fallback only covered "no data at
    // all"; a response that arrives with a field MISSING passed straight through as
    // `undefined`, and `AnalyticsBlock` calls `formatNumber(stat)` → `.toString()` on it,
    // which throws and takes the whole tab down to the error boundary. That is exactly what
    // happened running this build against an API that predates the possession fields — a
    // version skew between a deployed frontend and a rolling backend does the same thing in
    // production. A card is allowed to read 0; a page is not allowed to disappear.
    cards: { ...EMPTY_CARDS, ...(query.data?.cards ?? {}) },
    rows: query.data?.rows ?? [],
    isLoading: query.isLoading,
    refetch: query.refetch,
  }
}
